import * as React from "react";
import { cn } from "@finster/ui";
import type { SelectedSkillReference } from "../types";
import { buildUserMessageSkillContent } from "../userMessageContent";
import { SkillChip } from "./SkillChip";

const EMPTY_SKILLS: readonly SelectedSkillReference[] = [];

export interface UserMessageSkillContentProps {
  content: string;
  skills?: readonly SelectedSkillReference[];
  renderText: (text: string) => React.ReactNode;
}

/**
 * Sent-message renderer for selected Skills. Matching `/slug` tokens become
 * Skill chips in place. Skills that were selected without a token stay on a
 * row above the remaining prompt.
 */
export const UserMessageSkillContent: React.FC<UserMessageSkillContentProps> = ({
  content,
  skills = EMPTY_SKILLS,
  renderText,
}) => {
  if (skills.length === 0) {
    return content.length > 0 ? <div className="break-words">{renderText(content)}</div> : null;
  }

  const { prepended, segments, hasInlineSkill } = buildUserMessageSkillContent(content, skills);
  const showPrependedRow = prepended.length > 0;
  const showBody = hasInlineSkill || content.length > 0;

  return (
    <>
      {showPrependedRow ? (
        <div aria-label="Skills used" className={cn("flex flex-wrap gap-1.5", showBody && "mb-2")}>
          {prepended.map((skill) => (
            <SkillChip key={skill.id} skill={skill} density="compact" />
          ))}
        </div>
      ) : null}
      {hasInlineSkill ? (
        <div className="break-words">
          {segments.map((segment, index) =>
            segment.type === "skill" ? (
              <SkillChip
                key={segment.skill.id}
                skill={segment.skill}
                density="compact"
                className="align-middle"
              />
            ) : (
              <span key={`text-${index}`} className="whitespace-pre-wrap">
                {segment.text}
              </span>
            )
          )}
        </div>
      ) : content.length > 0 ? (
        <div className="break-words">{renderText(content)}</div>
      ) : null}
    </>
  );
};
